import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, OnDestroy, OnInit, signal } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { IXSERV_SERVICE_VERSION } from '@shared/constants';
import { BehaviorSubject, distinctUntilChanged, first, map, Observable, of, Subject, switchMap, takeUntil, timer } from 'rxjs';
import { ErrorPageReasons, EventTrackerNavigationReasons } from 'src/app/shared/constants';

export const REDIRECT_TIMER_MS = 6_000;
export const STEP_MS = 100;

@Component({
    selector: 'ixlab-error-page',
    imports: [
        // Angular
        CommonModule,
        RouterModule,
        // Angular Material
        MatCardModule,
        MatIconModule,
        MatButtonModule,
        MatProgressBarModule,
        // Own
    ],
    templateUrl: './error-page.component.html',
    styleUrl: './error-page.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ErrorPageComponent implements OnInit, OnDestroy {
    readonly #ngUnsubscribe = new Subject<void>();

    protected readonly logoutUrlObs = this._route.queryParamMap.pipe(map((paramMap) => paramMap.get('logoutUrl')));

    public logoutUrl?: string;

    #resetRedirectTimerSubject = new BehaviorSubject<void>(undefined);
    #redirectUrl = signal<string | null>(null);

    /**
     * Counting up from 0 to 100. At 100, redirect should be enabled.
     * @see {@linkcode isRedirectCooledDownObs}
     */
    protected readonly redirectTimerObs = this.#resetRedirectTimerSubject.pipe(
        switchMap(() => {
            const start = Date.now();

            return timer(0, STEP_MS).pipe(
                map(() => Date.now() - start),
                map((elapsedMs) => Math.max(0, Math.min(100, 100 * (elapsedMs / REDIRECT_TIMER_MS)))),
                distinctUntilChanged(),
            );
        }),
    );

    /**
     * Emit if {@linkcode redirectTimerObs} has reached 100 and therefore the redirect should be enabled.
     */
    protected readonly isRedirectCooledDownObs = this.redirectTimerObs.pipe(
        map((timer) => timer >= 100),
        distinctUntilChanged(),
    );

    constructor(
        private readonly _route: ActivatedRoute,
        private readonly _router: Router,
    ) {}

    ngOnInit() {
        this.isRedirectCooledDownObs
            .pipe(
                switchMap((isRedirectCooledDown) => {
                    if (!isRedirectCooledDown) {
                        return of(null);
                    }

                    return this._route.queryParamMap.pipe(map((paramMap) => paramMap.get('redirect') ?? '/'));
                }),
                takeUntil(this.#ngUnsubscribe),
            )
            .subscribe((url) => {
                this.#redirectUrl.set(url);
            });

        this.logoutUrlObs.pipe(first()).subscribe((logoutUrl) => {
            if (!logoutUrl) {
                return;
            }

            this.logoutUrl = logoutUrl;
        });
    }

    ngOnDestroy() {
        this.#ngUnsubscribe.next();
        this.#ngUnsubscribe.complete();
    }

    protected logoutAndRedirect() {
        this.logoutUrlObs.pipe(first()).subscribe((logoutUrl) => {
            if (!logoutUrl) {
                return;
            }

            window.location.href = logoutUrl;
        });
    }

    protected redirectToProvidedUrl() {
        const redirectUrl = this.#redirectUrl();
        if (redirectUrl === null) {
            return;
        }

        this.#resetRedirectTimer();
        this._router.navigateByUrl(redirectUrl, { state: { navigatedBy: EventTrackerNavigationReasons.ErrorPageButton } });
    }

    protected get errorInfoObs(): Observable<{ title: string; paragraphs: string[] } | undefined> {
        return this.#reasonAndMsgQueryParamsObs.pipe(
            map(({ reason, msg }) => {
                if (reason === ErrorPageReasons.ServiceVersion) {
                    return {
                        title: 'Serviceversion wird nicht unterstützt',
                        paragraphs: [`Serviceversion ${IXSERV_SERVICE_VERSION} wird vom ix.serv nicht unterstützt`],
                    };
                }

                if (reason === ErrorPageReasons.Practice) {
                    return {
                        title: 'Keine Praxiszuordnung gefunden',
                        paragraphs: ['Es konnte keine Praxiszuordnung gefunden werden.'],
                    };
                }

                if (reason === ErrorPageReasons.SsoInit) {
                    return {
                        title: 'SSO-Anmeldung fehlgeschlagen',
                        paragraphs: ['Die Anmeldung per SSO ist fehlgeschlagen. Bitte versuchen Sie es später erneut.'],
                    };
                }

                if (reason === ErrorPageReasons.Config) {
                    return {
                        title: 'Fehlerhafte Konfiguration',
                        paragraphs: ['Die Konfiguration ist fehlerhaft. Bitte kontaktieren Sie Ihren Administrator.'],
                    };
                }

                if (reason === ErrorPageReasons.CustomMessage) {
                    return {
                        title: 'Ein Fehler ist aufgetreten',
                        paragraphs: [msg || 'Ein Fehler ist aufgetreten'],
                    };
                }

                return undefined;
            }),
        );
    }

    get #reasonAndMsgQueryParamsObs() {
        return this._route.queryParamMap.pipe(
            map((paramMap) => ({
                reason: paramMap.get('reason'),
                msg: paramMap.get('msg'),
            })),
        );
    }

    #resetRedirectTimer() {
        this.#resetRedirectTimerSubject.next();
    }
}
